/**
 * Whose ground a host may stand on.
 *
 * The board already refused to let an army walk into a crown it was at peace
 * with — `marchProblem` had a clause saying so, and `landingRefusal` had the
 * same clause for a beach. That was a wall rather than a decision: there was no
 * way to *obtain* passage short of declaring war, allies could not march
 * together, a vassal could not cross its own liege's ground, and the AI's
 * pathfinder planned routes with a breadth-first search that knew nothing about
 * any of it — so a crown at war with somebody two provinces away would pick a
 * target, be refused the first step, and stand still for the rest of the
 * campaign.
 *
 * This file is the rule, in one predicate, with the pathfinding that has to
 * agree with it.
 *
 * ── The model ──────────────────────────────────────────────────────────────
 *
 * Five ways onto ground that is not yours, and there are deliberately no others:
 *
 *   1. **Nobody holds it.** Unclaimed ground is a commons.
 *   2. **One crown.** You and the owner are under the same head — liege, vassal
 *      or junior partner. A realm that cannot move inside itself is not a realm.
 *   3. **War.** You may enter the ground of a crown you are at war with. This is
 *      the old rule and it is the one that makes a war *about* territory.
 *   4. **A grant of passage.** Asked for, given, indefinite, revocable. The new
 *      thing, and the reason any of the rest is a decision.
 *   5. **An ally, in the war you are both in.** Not an ally unconditionally: an
 *      alliance is not a standing right to garrison your friend's capital. It is
 *      passage for as long as you are fighting the same people, which is exactly
 *      when it is needed and exactly when refusing it would be absurd.
 *
 * ── What was rejected ──────────────────────────────────────────────────────
 *
 * **A `bordersClosed` flag per nation.** The obvious shape, and it is a third
 * state that changes nothing: with no grant standing, the border is already
 * shut. It would need its own field, its own hash entry, its own save default,
 * its own button and its own AI clause, and at the end of it the set of things
 * a host may do would be identical. What the brief actually wants out of "closed
 * borders as a hostile act" is that *refusing* costs something — and that is a
 * property of the refusal, not of a flag. See `OP_REFUSED_PASSAGE`.
 *
 * **Passage as a `Treaty`.** Treaties in this system are stored once per pair
 * with `a < b`, because every one of them is symmetric. Passage is not: letting
 * your neighbour's host cross your moors is not the same concession as being
 * let across theirs, and the asymmetry is most of what makes it interesting.
 * So it is its own sparse list, keyed by direction.
 *
 * **A separate grant for landings.** One right, not two. Splitting them buys one
 * extra decision and costs a whole second treaty; and the *interesting* property
 * comes free from keeping them together — a grant of passage to a crown with
 * hulls is a far larger concession than the same grant to a landlocked one,
 * because a fleet can put a host on any coast you own in a month, while a road
 * grant only opens the border you actually share. The sea itself stays open:
 * `sailRefusal` asks nobody's permission, because open water belongs to nobody.
 * It is the shore that has an owner.
 *
 * ── Imports, and why there are three of them ───────────────────────────────
 *
 * This file is imported by `armies.ts`, `fleets.ts`, `turn.ts` and `ai.ts` —
 * everything that moves. So it imports `types.ts`, `diplotypes.ts` and
 * `graph.ts` and nothing else, none of which import anything that imports this.
 * `atWar` is reimplemented here as the two-line index read it is rather than
 * imported from `state.ts`, which would close a cycle through `fleets.ts`; and
 * nothing in here builds a sentence, because that would need the name tables.
 * The sentences are in `armies.ts` and `diploacts.ts`, where the callers are.
 */

import { neighbours } from './graph.ts';
import { TRESPASS_GRACE, type AccessGrant } from './diplotypes.ts';
import type { CampaignState } from './types.ts';

// ── The two-line reads this file will not import ───────────────────────────

/** `Nation.atWar` is the O(1) index over the war list. See `refreshWarFlags`. */
function warring(c: CampaignState, a: number, b: number): boolean {
  if (a < 0 || b < 0 || a === b) return false;
  return (c.nations[a]?.atWar[b] ?? 0) === 1;
}

/**
 * The crown at the top of the chain above `n`, or `n` itself.
 *
 * Guarded against a cycle rather than trusting there cannot be one, for the same
 * reason `topLiege` in `diplomacy.ts` is — subjection is set from four places,
 * and a liege that is transitively its own subject would hang a march-legality
 * check, which is the worst possible place for an infinite loop.
 */
export function topCrown(c: CampaignState, n: number): number {
  let at = n;
  for (let guard = 0; guard < 16; guard++) {
    const up = c.nations[at]?.liege ?? -1;
    if (up < 0 || up === at) return at;
    at = up;
  }
  return at;
}

/** Two crowns under one head. They may not fight, and they march together. */
export function sameCrown(c: CampaignState, a: number, b: number): boolean {
  if (a === b) return true;
  if (a < 0 || b < 0) return false;
  return topCrown(c, a) === topCrown(c, b);
}

// ── Grants ─────────────────────────────────────────────────────────────────

/** Whether `granter` has given `grantee` the run of its ground. */
export function hasPassage(c: CampaignState, granter: number, grantee: number): boolean {
  return findGrant(c, granter, grantee) !== undefined;
}

export function findGrant(
  c: CampaignState,
  granter: number,
  grantee: number,
): AccessGrant | undefined {
  if (granter < 0 || grantee < 0 || granter === grantee) return undefined;
  return c.diplo.access.find((g) => g.from === granter && g.to === grantee);
}

/**
 * Write a grant down. Idempotent, and sorted, so two campaigns that reached the
 * same set of grants by different routes hash the same.
 */
export function addPassage(c: CampaignState, granter: number, grantee: number): boolean {
  if (granter === grantee || granter < 0 || grantee < 0) return false;
  if (!c.nations[granter]?.alive || !c.nations[grantee]?.alive) return false;
  if (hasPassage(c, granter, grantee)) return false;
  c.diplo.access.push({ from: granter, to: grantee, since: c.turn });
  sortPassage(c);
  return true;
}

export function removePassage(c: CampaignState, granter: number, grantee: number): boolean {
  const i = c.diplo.access.findIndex((g) => g.from === granter && g.to === grantee);
  if (i < 0) return false;
  c.diplo.access.splice(i, 1);
  return true;
}

/** Every grant either way involving `n` gone. Used when a crown dies or is annexed. */
export function dropAllPassage(c: CampaignState, n: number): void {
  c.diplo.access = c.diplo.access.filter((g) => g.from !== n && g.to !== n);
}

/** Who this crown has let in, ascending. For the panel. */
export function passageGrantedBy(c: CampaignState, granter: number): number[] {
  return c.diplo.access
    .filter((g) => g.from === granter && c.nations[g.to]?.alive)
    .map((g) => g.to)
    .sort((a, b) => a - b);
}

/** Whose ground this crown may cross by treaty, ascending. */
export function passageHeldBy(c: CampaignState, grantee: number): number[] {
  return c.diplo.access
    .filter((g) => g.to === grantee && c.nations[g.from]?.alive)
    .map((g) => g.from)
    .sort((a, b) => a - b);
}

function sortPassage(c: CampaignState): void {
  c.diplo.access.sort((x, y) => x.from - y.from || x.to - y.to);
}

// ── Co-belligerence ────────────────────────────────────────────────────────

/**
 * Both in the same war, on the same side.
 *
 * Read off the war list rather than off `atWar`, and the difference matters: two
 * crowns can each be at war with the same third party in two *separate* wars,
 * which makes them fellow-travellers rather than allies and does not open
 * anybody's border. The list is short — one war at a time per crown, by the
 * council's own rule — so this is a walk of a handful of entries.
 */
export function coBelligerent(c: CampaignState, a: number, b: number): boolean {
  if (a === b || a < 0 || b < 0) return false;
  for (const war of c.diplo.wars) {
    if (war.attackers.includes(a) && war.attackers.includes(b)) return true;
    if (war.defenders.includes(a) && war.defenders.includes(b)) return true;
  }
  return false;
}

// ── The predicate everything asks ──────────────────────────────────────────

/**
 * Whether `nation` may legally stand in `province`.
 *
 * The single source of truth for the whole feature. `marchProblem` asks it about
 * the far end of a road, `landingRefusal` asks it about a beach, `updateSupply`
 * asks it about the ground a host is already standing on, and the AI's
 * pathfinder asks it about every province on the board. One function, so the
 * button the player sees greyed and the rule the simulation keeps are the same
 * rule by construction.
 */
export function mayEnter(c: CampaignState, nation: number, province: number): boolean {
  if (province < 0 || province >= c.provinces.length) return false;
  const owner = c.provinces[province].owner;
  if (owner < 0) return true;
  if (owner === nation) return true;
  if (sameCrown(c, nation, owner)) return true;
  if (warring(c, nation, owner)) return true;
  if (hasPassage(c, owner, nation)) return true;
  if (coBelligerent(c, nation, owner)) return true;
  return false;
}

// ── Standing somewhere you have no right to be ─────────────────────────────

/**
 * A host on ground it may not enter.
 *
 * This is the case the whole feature turns on, because it is the one that
 * happens constantly and by itself: **every peace strands both sides' armies on
 * each other's territory.** So does a revoked grant, so does the end of a shared
 * war between allies, so does a vassal being released. Before this, a host left
 * behind by a peace simply stayed — fed to full every month by
 * `updateSupply`, which counted any non-hostile owner as friendly — and squatted
 * on a foreign province until the campaign ended.
 *
 * Four answers were on the table and three of them are absurd. Teleporting the
 * host home hands out a free extraction and makes a deep raid costless.
 * Destroying it turns a diplomatic event into an unanswerable one. Leaving it
 * there is what the board did and is what makes the whole feature decoration.
 *
 * So: **grace, then hunger.** For `TRESPASS_GRACE` months nothing happens and
 * the host may walk out unimpeded — including *through* the ground of the crown
 * that has just closed its border, which is `withdrawStep`'s whole job and is
 * what stops a host being sealed in a province with no legal move. After that it
 * is trespassing: the countryside stops feeding it, which runs it down through
 * the exact machinery a siege already uses and eventually kills it, and the
 * crown whose ground it is standing on thinks worse of its owner every month it
 * stays. Nobody has to declare anything, no new battle path exists, and the
 * player's answer is always the same and always available: leave.
 */
export function isTrespassing(army: { aboard: number; trespassT: number }): boolean {
  if (army.aboard >= 0) return false;
  return army.trespassT > TRESPASS_GRACE;
}

/** Months of grace a host still has before the countryside turns on it. */
export function graceLeft(army: { trespassT: number }): number {
  return Math.max(0, TRESPASS_GRACE - army.trespassT + 1);
}

/**
 * Standing on ground it may not enter, grace or not. The state, before the clock.
 *
 * A host on the road is not intruding on anything: it is between two provinces,
 * which is the same distinction `armiesStandingIn` draws and for the same
 * reason. A host aboard a hull is not on the board at all.
 */
export function isIntruding(
  c: CampaignState,
  army: { owner: number; at: number; aboard: number; moveTo: number },
): boolean {
  if (army.aboard >= 0) return false;
  if (army.moveTo >= 0) return false;
  return !mayEnter(c, army.owner, army.at);
}

// ── Getting out ────────────────────────────────────────────────────────────

/**
 * Hops from every province to the nearest ground this crown may legally stand
 * on. Zero where it already may.
 *
 * A multi-source breadth-first search from every enterable province at once,
 * which is one pass over the graph rather than one per candidate. Unreachable
 * comes back as `-1`, which can genuinely happen: a host landed on an island
 * that belongs entirely to a crown that has just closed its border has nowhere
 * on that island to walk to. It is not sealed in — it simply has to be lifted
 * off, and its owner has a fleet or it does not.
 *
 * Not cached. It is a function of who owns what and who has granted what, both
 * of which move, and it is only ever reached for a host that is already standing
 * somewhere it should not be — which is rare enough that a cache would be a
 * correctness risk taken for no measurable gain. See the note at the top of
 * `graph.ts` about what a cache is allowed to be.
 */
export function exitDistances(c: CampaignState, nation: number): number[] {
  const dist = new Array<number>(c.provinces.length).fill(-1);
  const queue: number[] = [];
  for (let p = 0; p < c.provinces.length; p++) {
    if (!mayEnter(c, nation, p)) continue;
    dist[p] = 0;
    queue.push(p);
  }
  for (let head = 0; head < queue.length; head++) {
    const at = queue[head];
    for (const n of neighbours(c, at)) {
      if (dist[n] !== -1) continue;
      dist[n] = dist[at] + 1;
      queue.push(n);
    }
  }
  return dist;
}

/**
 * Whether a step from `from` to `to` is a step *out*.
 *
 * The rule that makes closed borders a decision rather than a trap: **a host
 * that has no right to stand where it stands may always walk toward ground where
 * it has.** Without this, a crown could close its border around a host standing
 * three provinces deep inside it — every neighbour of every province on the way
 * belonging to that same crown — and the host would have no legal order at all.
 * It would stand there and starve, having been given no move that could save it.
 * That is precisely the absurd result the case was flagged for.
 *
 * Strictly decreasing, so there is no loitering: a withdrawal has to get closer
 * to the door every month, and a host cannot use the rule to wander sideways
 * across somebody's country while claiming to be leaving.
 */
export function isWithdrawalStep(
  c: CampaignState,
  nation: number,
  from: number,
  to: number,
): boolean {
  if (mayEnter(c, nation, from)) return false;
  const dist = exitDistances(c, nation);
  const here = dist[from];
  const there = dist[to];
  if (here < 0 || there < 0) return false;
  return there < here;
}

/**
 * The province a host standing on closed ground should walk to, or -1.
 *
 * Ties break toward the lowest index, which is arbitrary and — the part that
 * matters — the same on every run of the same board.
 */
export function withdrawStep(c: CampaignState, nation: number, from: number): number {
  if (mayEnter(c, nation, from)) return -1;
  const dist = exitDistances(c, nation);
  const here = dist[from];
  if (here <= 0) return -1;
  let best = -1;
  for (const n of neighbours(c, from)) {
    if (dist[n] < 0 || dist[n] >= here) continue;
    if (best < 0 || n < best) best = n;
  }
  return best;
}

// ── Pathfinding that knows about borders ───────────────────────────────────

/**
 * Hops from `from` to every province this crown could actually walk to.
 *
 * `hopsFrom` in `graph.ts` answers a question about roads. This answers the
 * question the AI actually has, which is where its hosts can *go* — and the two
 * were silently different the moment borders meant anything. A council that
 * plans with road hops picks a target behind somebody else's country, orders the
 * first step, is refused by `canMarch`, and does nothing; measured against a
 * board where most crowns are at peace with most of their neighbours, that is
 * most of the map most of the time.
 *
 * The origin is always reachable at zero even when the host is standing
 * somewhere it may not be, because a host caught out by a peace still has to be
 * able to plan its way home.
 */
export function passageHops(c: CampaignState, nation: number, from: number): number[] {
  const dist = new Array<number>(c.provinces.length).fill(-1);
  if (from < 0 || from >= dist.length) return dist;
  dist[from] = 0;
  const queue = [from];
  for (let head = 0; head < queue.length; head++) {
    const at = queue[head];
    for (const n of neighbours(c, at)) {
      if (dist[n] !== -1) continue;
      if (!mayEnter(c, nation, n)) continue;
      dist[n] = dist[at] + 1;
      queue.push(n);
    }
  }
  return dist;
}

/**
 * The next province to step to along a shortest *legal* path, or -1.
 *
 * Computed by running the search backward from the destination, exactly as
 * `stepToward` in `graph.ts` does, so the path it walks is the path
 * `passageHops` measured. Note the asymmetry that makes running it backward
 * correct anyway: passage is a property of the ground being entered, and a route
 * that is legal in one direction is legal in the other — with the single
 * exception of the origin, which is why the origin is seeded unconditionally
 * above and why the first step out is tested against `canMarch` by the caller
 * rather than assumed.
 */
export function passageStep(
  c: CampaignState,
  nation: number,
  from: number,
  to: number,
): number {
  if (from === to) return from;
  const dist = passageHops(c, nation, to);
  if (dist[from] < 0) return -1;
  let best = -1;
  let bestD = dist[from];
  for (const n of neighbours(c, from)) {
    if (!mayEnter(c, nation, n)) continue;
    if (dist[n] >= 0 && dist[n] < bestD) {
      bestD = dist[n];
      best = n;
    }
  }
  return best;
}

/**
 * Whether opening `through`'s borders would put any of `target`'s ground within
 * walking distance of anything `nation` holds.
 *
 * The question a crown asks before it sends a herald: *is this the country in my
 * way?* Answered by running the same search with one extra crown's ground
 * treated as open, and comparing what it reaches. One breadth-first search per
 * candidate neighbour per war, and the council only asks it of crowns it already
 * borders, so the cost is linear in the border graph like everything else there.
 *
 * Deliberately does not ask whether the war is winnable or the target worth
 * taking. Those are `considerWar`'s questions and were answered before the war
 * was declared; this one is only about whether there is a road.
 */
export function passageWouldOpenARoad(
  c: CampaignState,
  nation: number,
  through: number,
  target: number,
): boolean {
  if (through === nation || through === target) return false;
  const origins = provincesHeldBy(c, nation);
  if (origins.length === 0) return false;

  const open = (p: number): boolean =>
    mayEnter(c, nation, p) || c.provinces[p].owner === through;

  // One search from all of this crown's ground at once, twice: once as the board
  // stands and once with the candidate's country opened. What changes is the
  // answer.
  const reachNow = floodFrom(c, origins, (p) => mayEnter(c, nation, p));
  const reachThen = floodFrom(c, origins, open);
  let gained = false;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== target) continue;
    // The target's ground is *enterable* either way — there is a war on — so what
    // this tests is whether anything leading to it opened up. Reachability, not
    // permission, is what the candidate's border was standing in the way of.
    if (!reachNow[p] && reachThen[p]) gained = true;
  }
  return gained;
}

/**
 * Whether any host of `nation` could walk at `target` at all, borders as they
 * stand.
 *
 * One multi-source search from every province this crown holds, not one search
 * per province — which is what the first version did, and at forty provinces
 * held it was forty breadth-first searches per war per crown per month for an
 * answer a single flood gives exactly.
 */
export function canReachOnFoot(c: CampaignState, nation: number, target: number): boolean {
  const origins = provincesHeldBy(c, nation);
  if (origins.length === 0) return false;
  const reach = floodFrom(c, origins, (p) => mayEnter(c, nation, p));
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner === target && reach[p]) return true;
  }
  return false;
}

function provincesHeldBy(c: CampaignState, nation: number): number[] {
  const out: number[] = [];
  for (let p = 0; p < c.provinces.length; p++) if (c.provinces[p].owner === nation) out.push(p);
  return out;
}

function floodFrom(
  c: CampaignState,
  origins: readonly number[],
  passable: (p: number) => boolean,
): boolean[] {
  const seen = new Array<boolean>(c.provinces.length).fill(false);
  const queue: number[] = [];
  for (const p of origins) {
    if (seen[p]) continue;
    seen[p] = true;
    queue.push(p);
  }
  for (let head = 0; head < queue.length; head++) {
    for (const n of neighbours(c, queue[head])) {
      if (seen[n] || !passable(n)) continue;
      seen[n] = true;
      queue.push(n);
    }
  }
  return seen;
}
